import { EventTriggerClient } from "@formant/core/client/EventTriggerClient";
import { defined } from "@formant/core/common/defined";
import { EventMatcher } from "@formant/core/common/EventMatcher";
import { IEventTrigger } from "@formant/core/model/IEventTrigger";
import { Uuid } from "@formant/core/model/Uuid";

import { IAuthStore } from "./IAuthStore";
import { StoreCache } from "./StoreCache";

export class EventTriggerStore {
    private cache = new StoreCache<Uuid, IEventTrigger | false>();
    private listCache = new StoreCache<void, IEventTrigger[]>();

    constructor(
        private eventTriggerClient: EventTriggerClient,
        private authStore: IAuthStore,
    ) {}

    public get(id: Uuid): IEventTrigger | false | undefined {
        return this.cache.get(
            id,
            async () =>
                (await this.eventTriggerClient.get(
                    await this.authStore.token(),
                    id,
                )) || false,
        );
    }

    public list(): IEventTrigger[] | undefined {
        return this.listAll()?.filter(_ => _.enabled);
    }

    public listAll(): IEventTrigger[] | undefined {
        return this.listCache.get(undefined, () => this.fetchList());
    }

    public async fetchList(): Promise<IEventTrigger[]> {
        const value = await this.eventTriggerClient.list(
            await this.authStore.token(),
        );
        this.listCache.set(undefined, value);
        return value;
    }

    public async create(eventTrigger: IEventTrigger): Promise<IEventTrigger> {
        const created = await this.eventTriggerClient.create(
            await this.authStore.token(),
            eventTrigger,
        );
        this.cache.set(defined(created.id), created);
        this.listCache.clear();
        return created;
    }

    public async update(id: Uuid, device: Partial<IEventTrigger>) {
        const updated = await this.eventTriggerClient.update(
            await this.authStore.token(),
            id,
            device,
        );
        this.cache.set(id, updated);
        this.listCache.clear();
        return updated;
    }

    public async disable(id: Uuid) {
        return await this.update(id, { enabled: false });
    }

    public listForStreamNames(streams: string[]) {
        const eventTriggers = this.list();
        if (!eventTriggers) {
            return undefined;
        }
        const eventMatcher = new EventMatcher();
        return eventTriggers.filter(eventTrigger =>
            streams
                .map(
                    _ =>
                        eventTrigger.condition?.stream &&
                        eventMatcher.matchesStream(
                            _,
                            eventTrigger.condition?.stream,
                        ),
                )
                .reduce((a, b) => a || b, false),
        );
    }

    public async save(eventTrigger: IEventTrigger) {
        const created = await this.create({
            ...eventTrigger,
            id: undefined,
        });
        if (eventTrigger.id !== undefined) {
            await this.disable(eventTrigger.id);
        }
        return created;
    }
}
